// src/claims/harvestRule.js — THE HARVEST (PRODUCTION) CLAIM PRICE AND SHORTFALL RULE.
// One rule, every caller: the Claims Manager roster and the single-farm Claim Check (both
// through computeHarvestClaim in claimEstimate.js) and the John Deere Claim Watch screener
// in api/_jd.js.
//
// CommonJS on purpose, like ppStages.js: api/_jd.js require()s it, the ESM claim modules
// import it (webpack handles both), and tools/_load-strategy.js requires it as-is.
//
// ── SOURCED ──────────────────────────────────────────────────────────────────
// Commodity Exchange Price Provisions (CEPP): the harvest price is capped at 200% of the
// projected price. 7 CFR 457.8 §3 / the Revenue Protection provisions: an RP guarantee is
// valued at the HIGHER of the projected and harvest prices; RP with the harvest price
// exclusion (RP-HPE) and Yield Protection (and APH) stay at the projected price. Revenue to
// count is production x harvest price. These are the rules computeHarvestClaim has carried
// since it shipped; they are moved here, not re-derived.
//
// ⚠ QUALITY ADJUSTMENT IS NOT MODELLED. Production here is the quantity a caller hands in;
// test weight, damage and the quality factors that reduce production to count are not
// applied anywhere in this app. It is a NAMED absence, returned on every result.

"use strict";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));

const QUALITY_ABSENCE = "quality factors not modelled";

// The harvest price after the 200% CEPP cap. Null stays null.
function cappedHarvestPrice(projected, harvestPx) {
  const p = num(projected), h = num(harvestPx);
  if (h == null) return null;
  return p != null && p > 0 ? Math.min(h, 2 * p) : h;
}

// The price the guarantee is valued at. `basis` is coverageBasis(plans).basis
// ("revenue" | "yield" | "mixed" | "unknown"); `hpe` is true when every plan excludes the
// harvest price. Only a YIELD basis or HPE is held at projected — a mixed or unknown basis
// keeps the revenue rule, exactly as both callers did before this was shared.
function guaranteePrice(projected, harvestPx, basis, hpe) {
  const p = num(projected) || 0;
  const hp = cappedHarvestPrice(projected, harvestPx);
  if (hp == null) return p;
  if (basis === "yield" || hpe === true) return p;
  return Math.max(p, hp);
}

// The whole shortfall arithmetic for one guarantee and one production figure.
//   guarBu      bushel guarantee (planted acres only — see summarizeClaimCoverage)
//   production  production to count, already summed by the caller from its sources
// Returns the figures every screen prints, plus estIndemnity and the named absence.
function harvestShortfall({ guarBu, production, projected, harvestPx, basis, hpe }) {
  const g = num(guarBu) || 0;
  const prod = num(production) || 0;
  const p = num(projected) || 0;
  const hp = cappedHarvestPrice(projected, harvestPx);
  const guarPrice = guaranteePrice(projected, harvestPx, basis, hpe);
  const isYP = basis === "yield";
  const revGuar = g * guarPrice;
  const revToCount = hp != null ? prod * hp : null;
  const buShort = g > 0 ? Math.max(0, g - prod) : null;
  const revShort = revToCount != null ? Math.max(0, revGuar - revToCount) : null;
  // A revenue policy pays on the revenue shortfall; a yield policy (or any policy before a
  // harvest price exists) on the bushel shortfall at the projected price.
  const ypIndemnity = buShort != null && p ? buShort * p : null;
  const estIndemnity = isYP ? ypIndemnity : (revShort != null ? revShort : ypIndemnity);
  return { guarPrice, harvestUsed: hp, revGuar, revToCount, buShort, revShort, estIndemnity,
           isYP, absences: [QUALITY_ABSENCE] };
}

// Guarantee bushels are on PLANTED acres (#448): prevent-plant acres are paid as prevent
// plant and never carry a harvest guarantee. A screener that only has insured acres and a
// prevent-plant figure takes the planted share here rather than re-deriving it.
function plantedAcresOf(insuredAcres, ppAcres) {
  const ins = num(insuredAcres) || 0;
  const pp = Math.max(0, Math.min(ins, num(ppAcres) || 0));
  return ins - pp;
}

// Ticket-vs-machine disagreement. Kevin's approved threshold: more than 10% (2026-10-18).
// Exactly 10% is NOT a disagreement. Machine data is a CHECK — it is never added to
// production.
const MACHINE_DISAGREE_PCT = 0.10;
function machineDisagreement(measuredBu, machineBu) {
  const m = num(measuredBu), j = num(machineBu);
  if (!(m > 0) || !(j > 0)) return { diffPct: null, flag: false };
  const diffPct = Math.abs(j - m) / m;
  // Decided in integer thousandths of a bushel, so a gap of exactly 10% cannot float either
  // side of the line: |machine - measured| x 1000 against measured x 100.
  const flag = Math.round(Math.abs(j - m) * 1000) > Math.round(m * 1000 * MACHINE_DISAGREE_PCT);
  return { diffPct, flag };
}

module.exports = { QUALITY_ABSENCE, MACHINE_DISAGREE_PCT, cappedHarvestPrice, guaranteePrice,
                   harvestShortfall, plantedAcresOf, machineDisagreement };
